/**
 * REUSABLE SERVICE - Auth Service
 * Authentication service that can be configured for any project
 *
 * Usage:
 * import { createAuthService } from '@/services/auth';
 *
 * const authService = createAuthService({
 *   tokenKey: 'my_app_token',
 *   userKey: 'my_app_user',
 *   rolePermissions: { ... }
 * });
 */

import { storage } from '../utils/helpers';

/**
 * Create an auth service with custom configuration
 * @param {Object} config - Configuration object
 * @returns {Object} Auth service instance
 */
export const createAuthService = (config = {}) => {
  const {
    tokenKey = 'auth_token',
    userKey = 'auth_user',
    rolePermissions = {
      super_admin: ['all'],
      admin: ['manage_users', 'manage_subscriptions', 'view_stats', 'manage_config'],
      support: ['view_users', 'view_subscriptions', 'view_stats'],
    },
  } = config;

  return {
    // Check if user is authenticated
    isAuthenticated() {
      const token = storage.get(tokenKey);
      const user = storage.get(userKey);
      return !!(token && user);
    },

    // Get current user
    getCurrentUser() {
      return storage.get(userKey);
    },

    // Get auth token
    getToken() {
      return storage.get(tokenKey);
    },

    // Save auth data
    saveAuthData(token, user) {
      storage.set(tokenKey, token);
      storage.set(userKey, user);
    },

    // Clear auth data
    clearAuthData() {
      storage.remove(tokenKey);
      storage.remove(userKey);
    },

    // Update user data
    updateUser(userData) {
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, ...userData };
        storage.set(userKey, updatedUser);
        return updatedUser;
      }
      return null;
    },

    // Check user role
    hasRole(role) {
      const user = this.getCurrentUser();
      if (!user) return false;
      if (Array.isArray(role)) {
        return role.includes(user.role);
      }
      return user.role === role;
    },

    // Check if super admin
    isSuperAdmin() {
      return this.hasRole('super_admin');
    },

    // Check if can perform action
    canPerformAction(action) {
      const user = this.getCurrentUser();
      if (!user) return false;

      const userPermissions = rolePermissions[user.role] || [];
      return userPermissions.includes('all') || userPermissions.includes(action);
    },

    // Check multiple permissions (AND logic)
    hasAllPermissions(permissions) {
      return permissions.every((permission) => this.canPerformAction(permission));
    },

    // Check multiple permissions (OR logic)
    hasAnyPermission(permissions) {
      return permissions.some((permission) => this.canPerformAction(permission));
    },

    // Get user permissions
    getUserPermissions() {
      const user = this.getCurrentUser();
      if (!user) return [];
      return rolePermissions[user.role] || [];
    },
  };
};

// Default instance for Zitelou Back Office
export const authService = createAuthService({
  tokenKey: 'admin_token',
  userKey: 'admin_user',
  rolePermissions: {
    super_admin: ['all'],
    admin: ['manage_users', 'manage_subscriptions', 'view_stats', 'manage_config'],
    support: ['view_users', 'view_subscriptions', 'view_stats'],
  },
});

export default authService;
